import {getSizedImageUrl} from '@shopify/theme-images';

import {ProductForm} from '../vendor/theme-scripts/theme-product-form';
import * as a11y from '../vendor/theme-scripts/theme-a11y';

import NativeScrollbar from './native-scrollbar';
import {Popout} from './popout';
import {Swatch} from './swatch';

const selectors = {
  elements: {
    html: 'html',
    body: 'body',
    sectionId: 'data-section-id',
    sectionOuter: '[data-section-id]',
    productGridItem: 'data-product-grid-item',
    formQuickAdd: '[data-form-quick-add]',
    quickAddLabel: 'data-quick-add-label',
    quickCollectionHande: 'data-collection-handle',
    selectOption: '[data-select-option]:not([data-quick-add-button])',
    holderFormQuickAdd: '[data-quick-add-holder]',
    goToNextElement: 'data-go-to-next',
    quickAddElement: 'data-quick-add-button',
    productJson: '[data-product-json]',
    productOptionsJson: '[data-product-options-json]',
    quickAddFormHolder: '[data-quick-add-form-holder]',
    featuredImageHolder: '[data-grid-slide]',
    productImagesHolder: '[data-product-image]',
    productInformationHolder: '[data-product-information]',
    scrollbarHolder: '[data-scrollbar]',
    scrollbarArrowPrev: '[data-scrollbar-arrow-prev]',
    scrollbarArrowNext: '[data-scrollbar-arrow-next]',
    radioOption: '[data-radio-option]',
    popoutWrapper: '[data-popout]',
    popupList: '[data-popout-list]',
    popupoutOption: '[data-popout-option]',
    popupoutOptionValue: 'data-value',
    popupoutToggle: '[data-popout-toggle]',
    selectPosition: 'data-select-position',
    swatch: 'data-swatch',
    backButton: '[data-back-button]',
    messageError: '[data-message-error]',
    idInput: '[name="id"]',
    buttonQuickAddMobile: '[data-button-quick-add-mobile]',
    ariaExpanded: 'aria-expanded',
    input: 'input',
    dataVariantTitle: 'data-variant-title',
  },
  classes: {
    active: 'is-active',
    select: 'is-selected',
    disable: 'is-disable',
    hide: 'is-hidden',
    added: 'is-added',
    loading: 'is-loading',
    visible: 'is-visible',
    error: 'has-error',
    focus: 'is-focused',
    popupoutVisible: 'popout-list--visible',
  },
  times: {
    debounce: 500,
    delay: 400,
    delaySmall: 200,
    delayMedium: 2000,
    delayLarge: 5000,
  },
  imageSize: '800x800',
};

const instances = [];

class QuickAddProduct {
  constructor(el) {
    this.cart = window.cart;
    this.a11y = a11y;
    this.themeAccessibility = window.accessibility;

    this.document = document;
    this.html = this.document.querySelector(selectors.elements.html);
    this.body = this.document.querySelector(selectors.elements.body);

    this.productGridItem = el;
    this.holder = this.productGridItem.querySelector(selectors.elements.holderFormQuickAdd);
    this.quickAddLabel = this.productGridItem.querySelector(`[${selectors.elements.quickAddLabel}]`);
    this.quickAddFormHolder = this.productGridItem.querySelector(selectors.elements.quickAddFormHolder);
    this.featuredImageHolder = this.productGridItem.querySelector(selectors.elements.featuredImageHolder);
    this.productInformationHolder = this.productGridItem.querySelector(selectors.elements.productInformationHolder);
    this.buttonQuickAddMobile = this.productGridItem.querySelector(selectors.elements.buttonQuickAddMobile);

    this.sectionOuter = this.productGridItem.closest(selectors.elements.sectionOuter);
    this.sectionId = this.sectionOuter.getAttribute(selectors.elements.sectionId);

    this.productJSON = null;
    this.productOptionsJSON = null;
    this.productForm = null;

    this.selectedOptions = [];
    this.filteredOptions = [];

    this.enableMobileMode = false;
    this.accessibilityStopEvent = false;
    this.quickAddFormIsLoaded = false;

    if (theme.settings.enableQuickAdd) {
      this.accessibility();
      this.show();
      this.hide();
      this.errorHandle();
    } else if (this.productGridItem) {
      this.productGridItem.addEventListener('mouseenter', () => this.addVariantImages());
    }
  }

  /**
   * Init native scrollbar for product options
   */
  initNativeScrollbar() {
    if (this.scrollbarHolder.length) {
      this.scrollbarHolder.forEach((scrollbar) => {
        new NativeScrollbar(scrollbar);
      });
    }
  }

  /**
   * Handle AJAX product grid item form
   */
  getForm() {
    if (this.quickAddFormIsLoaded) {
      return;
    }

    this.quickAddFormIsLoaded = true;
    const root = theme.routes.root === '/' ? '' : theme.routes.root;

    fetch(root + '/products/' + this.quickAddLabel.getAttribute(selectors.elements.quickAddLabel) + '?section_id=api-quick-add')
      .then((response) => response.text())
      .then((data) => {
        const fresh = document.createElement('div');
        fresh.innerHTML = data;
        const cleanResponse = fresh.querySelector('[data-api-content]').innerHTML;

        // Inject a unique ID into the form
        const collectionHandle = this.quickAddLabel.getAttribute(selectors.elements.quickCollectionHande);
        const unique = `${collectionHandle}-${this.sectionId}`;
        this.quickAddFormHolder.innerHTML = cleanResponse.replaceAll('||collection-index||', unique);

        this.loadForm();
      })
      .catch((e) => {
        console.warn(e);
      });
  }

  /**
   * Load form and cache elements
   */
  loadForm() {
    this.form = this.quickAddFormHolder.querySelector(selectors.elements.formQuickAdd);
    this.selectOption = this.quickAddFormHolder.querySelectorAll(selectors.elements.selectOption);
    this.productElemJSON = this.quickAddFormHolder.querySelector(selectors.elements.productJson);
    this.productOptionsElemJSON = this.quickAddFormHolder.querySelector(selectors.elements.productOptionsJson);
    this.scrollbarHolder = this.quickAddFormHolder.querySelectorAll(selectors.elements.scrollbarHolder);
    this.popoutWrapper = this.quickAddFormHolder.querySelectorAll(selectors.elements.popoutWrapper);
    this.swatches = this.quickAddFormHolder.querySelectorAll(`[${selectors.elements.swatch}]`);
    this.backButtons = this.quickAddFormHolder.querySelectorAll(selectors.elements.backButton);

    // Init Swatches
    if (this.swatches.length) {
      this.swatches.forEach((swatch) => {
        new Swatch(swatch);
      });

      this.changeVariantImageOnHover();
    }

    // Init Popout
    if (this.popoutWrapper.length) {
      this.popoutWrapper.forEach((wrapper) => {
        new Popout(wrapper);
      });
    }

    // Init native scrollbar
    this.initNativeScrollbar();

    // Init back buttons to return one step
    this.initGoToBack();

    const hasProductJSON = this.productElemJSON && this.productElemJSON.innerHTML !== '';
    const hasProductOptionsJSON = this.productOptionsElemJSON && this.productOptionsElemJSON.innerHTML !== '';

    if (hasProductJSON && hasProductOptionsJSON) {
      this.productJSON = JSON.parse(this.productElemJSON.innerHTML);
      this.productOptionsJSON = JSON.parse(this.productOptionsElemJSON.innerHTML);

      this.initForm();
    } else {
      console.error('Missing product JSON or product options with values JSON');
    }
  }

  /**
   * Init product form
   */
  initForm() {
    this.filterFirstOptionValues();

    this.productForm = new ProductForm(this.form, this.productJSON, {
      onOptionChange: this.onOptionChange.bind(this),
    });

    if (this.enableMobileMode) {
      this.addFirstVariantIsDefault(this.buttonQuickAddMobile.hasAttribute(selectors.elements.quickAddElement));

      this.buttonQuickAddMobile.classList.add(selectors.classes.hide);
      this.buttonQuickAddMobile.classList.remove(selectors.classes.loading);

      this.quickAddLabel.classList.add(selectors.classes.hide);

      this.toggleClasses();
    } else {
      this.quickAddLabel.classList.remove(selectors.classes.disable, selectors.classes.hide);
    }
  }

  /**
   * Init one step go to back
   */
  onOptionChange(event) {
    const targetElement = event.target;
    const optionHolder = targetElement.closest(selectors.elements.selectOption);
    const position = Number(optionHolder.getAttribute(selectors.elements.selectPosition));
    const firstOptionValue = event.dataset.variant ? event.dataset.variant.options[position - 1] : event.dataset.options[position - 1];
    const focusedIsEnable = this.body.classList.contains(selectors.classes.focus);

    this.changeVariantImage(event.dataset.variant);

    // Stop change when focus is enabled
    if (focusedIsEnable && this.accessibilityStopEvent) {
      return;
    }

    this.selectedOptions.push(event.target.value);

    optionHolder.classList.add(selectors.classes.select);

    this.filterAvailableOptions(firstOptionValue, position);

    if (targetElement.hasAttribute(selectors.elements.goToNextElement)) {
      optionHolder.classList.remove(selectors.classes.active);
      optionHolder.nextElementSibling.classList.add(selectors.classes.active);
    }

    if (targetElement.hasAttribute(selectors.elements.quickAddElement) && event.dataset.variant.available) {
      if (theme.settings.cartDrawerEnabled) {
        this.quickAddLabel.classList.remove(selectors.classes.hide);
        this.quickAddLabel.classList.add(selectors.classes.loading);
      }

      this.html.dispatchEvent(
        new CustomEvent('cart:add-to-cart', {
          bubbles: true,
          detail: {
            element: this.holder,
            label: this.quickAddLabel,
            data: {
              id: event.dataset.variant.id,
              quantity: 1,
            },
          },
        })
      );

      setTimeout(() => {
        this.selectedOptions = [];
        this.resetInputsOfInstance();
      }, selectors.times.delayMedium);

      optionHolder.classList.remove(selectors.classes.active);
    }

    if (focusedIsEnable && targetElement.hasAttribute(selectors.elements.goToNextElement)) {
      this.accessibilityTrapFocus(optionHolder.nextElementSibling);
    }

    this.accessibilityStopEvent = false;
  }

  /**
   * Init one-step go to back
   */
  initGoToBack() {
    if (this.backButtons.length) {
      this.backButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();

          const isFocused = this.body.classList.contains(selectors.classes.focus);

          // Remove last added option value
          this.selectedOptions.pop();

          const optionHolder = button.closest(selectors.elements.selectOption);

          let previousHolder = optionHolder.previousElementSibling.matches(selectors.elements.selectOption) ? optionHolder.previousElementSibling : this.quickAddLabel;

          if (previousHolder === this.quickAddLabel && isFocused) {
            this.themeAccessibility.lastFocused = this.quickAddLabel;
          }

          if (previousHolder === this.quickAddLabel && this.enableMobileMode) {
            previousHolder = this.buttonQuickAddMobile;
          }

          // Return inputs and selects to primary states
          if (previousHolder !== this.quickAddLabel) {
            if (isFocused) {
              this.accessibilityTrapFocus(previousHolder);
            }

            const inputs = optionHolder.querySelectorAll(selectors.elements.input);
            const selects = optionHolder.querySelectorAll(selectors.elements.popupoutOption);

            const previousInputs = previousHolder.querySelectorAll(selectors.elements.input);
            const previousSelects = previousHolder.querySelectorAll(selectors.elements.popupoutOption);

            if (inputs) {
              inputs.forEach((input) => {
                input.checked = false;
              });
            }

            if (selects) {
              selects.forEach((option) => {
                option.classList.remove(selectors.classes.visible);
              });
            }

            if (previousInputs) {
              previousInputs.forEach((input) => {
                input.checked = false;
              });
            }

            if (previousSelects) {
              previousSelects.forEach((option) => {
                option.classList.remove(selectors.classes.visible);
              });
            }
          }

          if (previousHolder !== this.buttonQuickAddMobile) {
            if (isFocused) {
              this.accessibilityTrapFocus(previousHolder);
            }

            previousHolder.classList.add(selectors.classes.active, selectors.classes.select);
          }

          if (previousHolder === this.buttonQuickAddMobile) {
            this.holder.classList.remove(selectors.classes.visible);
          }

          previousHolder.classList.remove(selectors.classes.hide);

          optionHolder.classList.remove(selectors.classes.active, selectors.classes.select);

          if (isFocused) {
            previousHolder.focus();
          }
        });
      });
    }
  }

  /**
   * Toggle classes
   */
  toggleClasses() {
    this.holder.classList.add(selectors.classes.visible);

    if (this.selectOption && this.selectOption.length && this.quickAddFormHolder.innerHTML !== '&nbsp;') {
      this.quickAddLabel.classList.add(selectors.classes.hide);

      const options = Array.from(this.selectOption);
      const hasActiveOption = options.filter((option) => {
        return option.classList.contains(selectors.classes.active);
      });

      if (hasActiveOption.length === 0) {
        this.selectOption[0].classList.add(selectors.classes.active);

        if (this.body.classList.contains(selectors.classes.focus) && this.accessibilityStopEvent) {
          setTimeout(() => {
            this.accessibilityTrapFocus(this.selectOption[0]);
          }, selectors.times.delaySmall);
        }
      }
    }
  }

  /**
   * Add first variant if it is the default variant
   *
   * @param { Boolean } targetHasQuickAddAttribute
   */
  addFirstVariantIsDefault(targetHasQuickAddAttribute) {
    const firstVariant = this.productJSON.variants[0];

    if (targetHasQuickAddAttribute && firstVariant.available) {
      if (theme.settings.cartDrawerEnabled) {
        this.quickAddLabel.classList.remove(selectors.classes.hide);
        this.quickAddLabel.classList.add(selectors.classes.loading);
      }

      this.html.dispatchEvent(
        new CustomEvent('cart:add-to-cart', {
          bubbles: true,
          detail: {
            element: this.holder,
            label: this.quickAddLabel,
            data: {
              id: firstVariant.id,
              quantity: 1,
            },
          },
        })
      );
    }
  }

  /**
   * Show quick add
   */
  show() {
    // Button quick view (touch devices)
    if (this.buttonQuickAddMobile) {
      this.buttonQuickAddMobile.addEventListener('click', () => {
        this.enableMobileMode = true;

        this.buttonQuickAddMobile.classList.add(selectors.classes.loading);

        if (this.quickAddFormHolder.innerHTML === '&nbsp;' && !this.quickAddFormIsLoaded) {
          this.getForm();
        } else {
          this.toggleClasses();

          this.buttonQuickAddMobile.classList.remove(selectors.classes.loading);
          this.buttonQuickAddMobile.classList.add(selectors.classes.hide);
          this.holder.classList.add(selectors.classes.visible);
        }

        if (this.productJSON !== null) {
          this.addFirstVariantIsDefault(this.buttonQuickAddMobile.hasAttribute(selectors.elements.quickAddElement));
        }
      });
    }

    // Do AJAX when hover on product grid item
    if (this.productGridItem) {
      this.productGridItem.addEventListener('mouseenter', () => {
        this.enableMobileMode = false;

        this.hideOtherHolders();

        if (this.quickAddFormHolder && this.quickAddFormHolder.innerHTML === '&nbsp;' && !this.quickAddFormIsLoaded) {
          this.getForm();
        }

        this.addVariantImages();
      });
    }

    if (this.quickAddLabel) {
      // Do AJAX when focus on quick add label
      this.quickAddLabel.addEventListener('focusin', () => {
        this.enableMobileMode = false;

        if (this.quickAddFormHolder.innerHTML === '&nbsp;' && !this.quickAddFormIsLoaded) {
          this.getForm();
        }
      });

      // Open the first product option values or
      // add product to the cart if it is enable
      this.quickAddLabel.addEventListener('click', () => {
        this.enableMobileMode = false;

        const stopEvent = this.quickAddLabel.classList.contains(selectors.classes.added) || this.quickAddLabel.classList.contains(selectors.classes.disable);

        if (stopEvent || this.productJSON === null) {
          return;
        }

        this.selectedOptions = [];

        this.addFirstVariantIsDefault(this.quickAddLabel.hasAttribute(selectors.elements.quickAddElement));

        if (this.quickAddFormHolder.innerHTML !== '&nbsp;') {
          this.toggleClasses();
        }

        this.addVariantImages();
      });
    }

    this.productInformationHolder.addEventListener('mouseenter', () => {
      this.featuredImageHolder.closest(selectors.elements.productImagesHolder).classList.remove(selectors.classes.visible);
    });
  }

  /**
   * Hide quick add
   */
  hide() {
    if (this.quickAddLabel || this.buttonQuickAddMobile) {
      if (theme.settings.cartDrawerEnabled) {
        this.document.addEventListener('theme:cart-close', () => {
          setTimeout(() => {
            this.resetButtonsOfInstance();
          }, selectors.times.delayLarge);
        });
      } else {
        setTimeout(() => {
          this.resetButtonsOfInstance();
        }, selectors.times.delayLarge);
      }
    }
  }

  accessibility() {
    this.productGridItem.addEventListener('keyup', (event) => {
      if (event.keyCode === window.theme.keyboardKeys.TAB) {
        this.accessibilityStopEvent = true;
      }

      if (event.keyCode === window.theme.keyboardKeys.ENTER) {
        this.accessibilityStopEvent = false;
        const element = event.target.hasAttribute(selectors.elements.popupoutOptionValue) ? event.target.closest(selectors.elements.popupList).nextElementSibling : event.target;

        element.dispatchEvent(new Event('change'));
      }
    });
  }

  /**
   * Enable trap focus
   *
   * @param { DOM Element } container
   */
  accessibilityTrapFocus(container) {
    this.a11y.removeTrapFocus();
    this.a11y.trapFocus(container, {
      elementToFocus: container.querySelector(selectors.elements.backButton),
    });
  }

  /**
   * Filter available options based on variants
   *
   * @param { String } value
   * @param { Number } optionIndex
   * @param { Boolean } enableCheck
   */
  filterAvailableOptions(value, optionIndex, enableCheck = true) {
    const variants = this.productJSON.variants.filter((variant) => {
      if (variant.options.length > 1) {
        let available = [];

        this.selectedOptions.forEach((option, index) => {
          available.push(variant[`option${optionIndex}`] === value && variant.options[index] === option && variant.available);
        });

        if (!available.includes(false) && available.length > 0) {
          return variant;
        }
      } else {
        return variant[`option${optionIndex}`] === value && variant.available;
      }
    });

    this.productOptionsJSON.forEach((option) => {
      if (option.position !== optionIndex) {
        const filteredObject = {};
        filteredObject.name = option.name;
        filteredObject.position = option.position;
        filteredObject.values = [];

        option.values.forEach((value) => {
          const availableVariants = variants.filter((variant) => variant[`option${option.position}`] === value);

          if (availableVariants.length) {
            filteredObject.values.push(value);
          }
        });

        this.filteredOptions = [...this.filteredOptions, filteredObject];
      }
    });

    if (enableCheck) {
      this.disableUnavailableValues();
      this.filteredOptions = [];
    }
  }

  /**
   * Filter first option values based on the variants
   * on load or when the product is added
   */
  filterFirstOptionValues() {
    this.productOptionsJSON[0].values.forEach((value) => {
      const variants = this.productJSON.variants.filter((variant) => {
        return variant.option1 === value && variant.available;
      });

      if (variants.length === 0 && this.selectOption[0] !== undefined) {
        const inputsArr = this.selectOption[0].querySelectorAll('input');
        const elementsArr = this.selectOption[0].querySelectorAll(`[${selectors.elements.popupoutOptionValue}]`);
        let currentInput = null;
        let currentOption = null;

        if (inputsArr.length) {
          for (const input of inputsArr) {
            if (input.value === value) {
              currentInput = input;
              break;
            }
          }
        }

        if (elementsArr.length) {
          for (const element of elementsArr) {
            if (element.getAttribute(selectors.elements.popupoutOptionValue) === value) {
              currentOption = element;
              break;
            }
          }
        }

        if (currentInput) {
          currentInput.checked = false;
          currentInput.disabled = true;
        }

        if (currentOption) {
          currentOption.classList.add(selectors.classes.disable);
          currentOption.setAttribute('tabindex', -1);
        }
      }
    });
  }

  /**
   * Disable/Enable option values
   */
  disableUnavailableValues() {
    if (this.selectOption) {
      this.selectOption.forEach((option) => {
        const inputs = option.querySelectorAll(selectors.elements.input);
        const selects = option.querySelectorAll(selectors.elements.popupoutOption);

        // Disable/Enable swatches and boxes
        inputs.forEach((input) => {
          if (!option.classList.contains(selectors.classes.select)) {
            input.checked = false;
            input.disabled = true;
          }

          this.filteredOptions.forEach((data) => {
            if (data.values.includes(input.value)) {
              input.disabled = false;
            }
          });
        });

        // Disable/Enable select options
        selects.forEach((item) => {
          if (!option.classList.contains(selectors.classes.select)) {
            item.setAttribute('tabindex', -1);
            item.classList.add(selectors.classes.disable);
          }

          this.filteredOptions.forEach((data) => {
            if (data.values.includes(item.getAttribute(`${selectors.elements.popupoutOptionValue}`))) {
              item.classList.remove(selectors.classes.disable);
              item.setAttribute('tabindex', 1);
            }
          });
        });
      });
    }
  }

  /**
   * Change variant image on hover
   */
  changeVariantImageOnHover() {
    this.swatches.forEach((swatch) => {
      swatch.addEventListener('mouseover', () => {
        for (let index = 0; index < this.productJSON.variants.length; index++) {
          const variant = this.productJSON.variants[index];

          if (variant.featured_media !== undefined && variant.options[this.selectedOptions.length] === swatch.getAttribute(selectors.elements.swatch)) {
            this.changeVariantImage(variant);
            break;
          }
        }
      });
    });
  }

  /**
   * Change image with a variant image if exists
   *
   * @param { Object } currentVariant
   */
  changeVariantImage(currentVariant) {
    const imageExists = currentVariant && currentVariant.featured_media && currentVariant.featured_media !== undefined;
    const currentVariantTitle = currentVariant.title.replaceAll('"', "'");

    if (imageExists) {
      const imageTarget = this.featuredImageHolder.querySelector(`[${selectors.elements.dataVariantTitle}="${currentVariantTitle}"]`);

      if (imageTarget) {
        const imageVisible = this.featuredImageHolder.querySelector(`[${selectors.elements.dataVariantTitle}].${selectors.classes.visible}`);
        if (imageVisible) {
          imageVisible.classList.remove(selectors.classes.visible);
        }
        imageTarget.classList.add(selectors.classes.visible);
      } else {
        const currentImage = getSizedImageUrl(currentVariant.featured_media.preview_image.src, selectors.imageSize);

        this.featuredImageHolder.style.setProperty('background-image', `url(${currentImage})`);
      }
      this.featuredImageHolder.closest(selectors.elements.productImagesHolder).classList.add(selectors.classes.visible);
    }
  }

  addVariantImages() {
    const images = this.productGridItem.querySelectorAll(`[${selectors.elements.dataVariantTitle}][style*="display: none;"]`);
    if (images.length) {
      images.forEach((image) => {
        image.style.removeProperty('display');
      });
    }
  }

  /**
   *  Reset option values
   *
   * @param { DOM Element } option
   */
  resetOptions(option) {
    option.querySelectorAll(selectors.elements.input).forEach((input) => {
      input.checked = false;
      input.disabled = false;
    });

    option.querySelectorAll(selectors.elements.popupoutOption).forEach((option) => {
      option.classList.remove(selectors.classes.disable);
      option.setAttribute('tabindex', 1);
    });
  }

  /**
   * Hide other holders if they are visible
   */
  hideOtherHolders() {
    const otherHolderFormQuickAdd = this.document.querySelectorAll(selectors.elements.holderFormQuickAdd);

    if (otherHolderFormQuickAdd) {
      otherHolderFormQuickAdd.forEach((holder) => {
        if (holder !== this.holder) {
          holder.classList.remove(selectors.classes.visible);
          const selectToggleButton = holder.querySelector(selectors.elements.popupoutToggle);

          if (selectToggleButton) {
            selectToggleButton.setAttribute(selectors.elements.ariaExpanded, false);
            selectToggleButton.nextElementSibling.classList.remove(selectors.classes.popupoutVisible);
          }

          if (this.popoutWrapper) {
            holder.closest(`[${selectors.elements.productGridItem}]`).querySelector(selectors.elements.productImagesHolder).classList.remove(selectors.classes.visible);
          }
        }
      });
    }
  }

  /**
   * Reset inputs of this instance
   */
  resetInputsOfInstance() {
    if (this.selectOption) {
      this.quickAddLabel.classList.remove(selectors.classes.hide);
      this.initNativeScrollbar();

      this.selectOption.forEach((option) => {
        option.classList.remove(selectors.classes.active, selectors.classes.select);
        const selectToggleButton = option.querySelector(selectors.elements.popupoutToggle);

        if (selectToggleButton) {
          selectToggleButton.innerText = theme.strings.selectValue;
          selectToggleButton.setAttribute(selectors.elements.ariaExpanded, false);
        }

        this.resetOptions(option);
      });

      this.filterFirstOptionValues();
      this.filteredOptions = [];
    }
  }

  /**
   * Reset buttons to default states
   */
  resetButtonsOfInstance() {
    this.quickAddLabel.classList.remove(selectors.classes.select, selectors.classes.added, selectors.classes.visible);

    this.buttonQuickAddMobile.classList.remove(selectors.classes.hide);
  }

  /**
   * Handle error cart response
   */
  errorHandle() {
    this.html.addEventListener('cart:add-to-error', (event) => {
      const holder = event.detail.holder;
      const errorMessageHolder = holder.querySelector(selectors.elements.messageError);

      holder.querySelector(`[${selectors.elements.quickAddLabel}]`).classList.remove(selectors.classes.visible, selectors.classes.added, selectors.classes.loading);

      holder.classList.add(selectors.classes.error);

      errorMessageHolder.innerText = event.detail.description;

      setTimeout(() => {
        holder.classList.remove(selectors.classes.error);
        holder.classList.add(selectors.classes.visible);
        holder.previousElementSibling.classList.remove(selectors.classes.hide);
      }, selectors.times.delayLarge);
    });
  }
}

const quickAddProduct = {
  onLoad() {
    this.container.querySelectorAll(`[${selectors.elements.productGridItem}]`).forEach((item) => {
      instances.push(new QuickAddProduct(item));
    });
  },
};

export {quickAddProduct, QuickAddProduct};
